import React, { useCallback, useRef } from 'react';
import Scrollbar from 'react-scrollbars-custom';
import useOnClickOutside from 'use-onclickoutside';
import PropTypes from 'prop-types';
import { AnalyticsInstance } from '@ts/utils/analytics';
import * as S from './styles';

export default function Dropdown({
    isOpen,
    onSelect,
    changeIsOpen,
    destinations,
    destination,
    destinationCategory,
    selectorRef,
}) {
    const scroll1Ref = useRef(0);
    const scroll2Ref = useRef(0);
    const onUpdateScroll1 = useCallback((v) => (scroll1Ref.current = v.scrollTop), [scroll1Ref]);
    const onUpdateScroll2 = useCallback((v) => (scroll2Ref.current = v.scrollTop), [scroll2Ref]);
    const closeDropdown = useCallback(() => changeIsOpen(false), [changeIsOpen]);
    useOnClickOutside(selectorRef, closeDropdown);

    const onSelectDestination = useCallback(
        (elem) => {
            if (typeof onSelect === 'function') {
                onSelect(elem, null);
            }
            AnalyticsInstance.navigation.selectedSearch({
                destination: elem,
            });
        },
        [onSelect]
    );

    const onSelectDestinationCategory = useCallback(
        (elem) => {
            if (typeof onSelect === 'function') {
                onSelect(destination, elem);
                changeIsOpen(false);
            }
            AnalyticsInstance.navigation.selectedSearch({
                destination,
                category: elem,
            });
        },
        [changeIsOpen, destination, onSelect]
    );

    const selectedDestination = destination;
    const selectedDestinationCategory = destinationCategory;
    const selectedDestinationId = (selectedDestination && selectedDestination.id) || null;
    const selectedDestinationCategoryId =
        (selectedDestinationCategory && selectedDestinationCategory.id) || null;

    return (
        <Animated isOpen={isOpen}>
            <S.Dropdown data-test-id="dropdown">
                <S.DropdownMenu data-test-id="destinations">
                    <Scrollbar scrollTop={scroll1Ref.current} onUpdate={onUpdateScroll1}>
                        {destinations.map((destObject) => (
                            <DropdownMenuItemSelectableElement
                                key={destObject.id}
                                element={destObject}
                                onSelect={onSelectDestination}
                                isSelected={selectedDestinationId === destObject.id}
                            >
                                {destObject.label}
                            </DropdownMenuItemSelectableElement>
                        ))}
                    </Scrollbar>
                </S.DropdownMenu>

                <React.Fragment>
                    {selectedDestination && (
                        <S.DropdownMenu data-test-id="categories">
                            <Scrollbar scrollTop={scroll2Ref.current} onUpdate={onUpdateScroll2}>
                                <DropdownMenuItemSelectableElement
                                    key="all"
                                    element={null}
                                    onSelect={onSelectDestinationCategory}
                                    isSelected={selectedDestinationCategoryId === null}
                                >
                                    All activities
                                </DropdownMenuItemSelectableElement>
                                {selectedDestination.categories.map((category) => (
                                    <DropdownMenuItemSelectableElement
                                        // prevent non-rendering on compared ids
                                        key={`${selectedDestinationId}_${category.id}`}
                                        element={category}
                                        onSelect={onSelectDestinationCategory}
                                        isSelected={selectedDestinationCategoryId === category.id}
                                    >
                                        {category.name}
                                    </DropdownMenuItemSelectableElement>
                                ))}
                            </Scrollbar>
                        </S.DropdownMenu>
                    )}
                </React.Fragment>
            </S.Dropdown>
        </Animated>
    );
}

Dropdown.propTypes = {
    selectorRef: PropTypes.any,
    isOpen: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
    changeIsOpen: PropTypes.func.isRequired,
    destinations: PropTypes.array.isRequired,
    destination: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
    destinationCategory: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
};

export const DropdownMenuItemSelectableElement = ({
    className,
    isSelected,
    onSelect,
    children,
    element,
}) => {
    const onClick = useCallback(() => {
        if (typeof onSelect === 'function') {
            onSelect(element);
        }
    }, [onSelect, element]);

    return (
        <S.DropdownMenuItem
            data-test-id="item"
            className={className}
            onClick={onClick}
            isSelected={isSelected}
        >
            {children}
        </S.DropdownMenuItem>
    );
};

DropdownMenuItemSelectableElement.propTypes = {
    onSelect: PropTypes.func,
    element: PropTypes.any,
    isSelected: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
};

export const Animated = ({ children, isOpen }) => {
    return (
        <S.Animated
            style={{
                ...(isOpen
                    ? { display: 'block', opacity: 1, top: 50 }
                    : { display: 'none', opacity: 0, top: 30 }),
                ...(isOpen ? {} : { pointerEvents: 'none' }),
            }}
        >
            {children}
        </S.Animated>
    );
};

Animated.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    children: PropTypes.node,
};
